import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import Button from '@/components/atoms/button';
import Image from '@/components/atoms/Image';
import QuantityWidget from '@/components/atoms/quantity-widget';
import { LineItem } from '@shared/types/cart';
import { PreferredDeliveryDate, RemoveButton, AddNote, CartInSufficientStockText } from './cart-options';
import Typography from '@/components/atoms/typography';
import Link from '@/components/atoms/link';
import Modal from '@/components/organisms/modal';
import { getGstMultiplier } from '@/utils/currency-helpers';

const MobileCartItem = ({
  lineItem,
  onUpdateQuantity,
  onRemove,
  sum,
  clearCart,
  onCheckout,
  showFooter,
  onAddCoupon,
  onRemoveCoupon,
  vouchers,
}: {
  lineItem: LineItem;
  onUpdateQuantity: Function;
  onRemove: Function;
  sum: any;
  clearCart: any;
  onCheckout: any;
  showFooter: boolean;
  onAddCoupon?: (discountCode: string) => Promise<any>;
  onRemoveCoupon?: (discountCode: string) => Promise<any>;
  vouchers?: Array<{
    code: string;
    discount: { amount: number; currency: string; displayAmount: string };
  }>;
}) => {
  const router = useRouter();
  const totalDiscount = (vouchers ?? []).reduce((acc, v) => acc + (v?.discount?.amount ?? 0), 0);
  const { variant, name, productId, price, count, totalPrice, _url, bundleProducts = [] } = lineItem;
  const colour = variant?.attributes?.colour;
  const availableQuantity = variant?.availableQuantity || 0;
  const [quantity, setQuantity] = useState<number>(count as number);
  const [primaryImg] = variant?.images ?? [];
  const sku = variant?.sku;
  const qtyDisabled = availableQuantity < quantity;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const { translate } = useTranslation();
  const variantKey = productId;
  const productType = lineItem?.isBrochure

  const quantityChangeHandler = (value: number) => {
    if (value > availableQuantity) {
      return;
    }
    setQuantity(value);
    if (value === 0) {
      onRemove(sku);
    } else {
      onUpdateQuantity(sku, value, variantKey);
    }
  };

  const removeHandler = () => {
    onRemove(sku);
  };

  useEffect(() => {
    const body = document.body;
    if (isModalOpen) {
      body.style.overflow = 'hidden';
      body.classList.add('modal-open');
    } else {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    }
    return () => {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onAvailableLimtExceeds = (rawValue: number) => {
    const isModalOpen = rawValue > availableQuantity;

    if (isModalOpen) {
      setIsModalOpen(isModalOpen);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-gray-300  px-0 py-4">
      <div className="!w-full">
        {' '}
        <Link link={_url ?? '#'}>
          {primaryImg ? (
            <Image src={primaryImg} alt={name ?? ''} className="w-full object-contain" />
          ) : (
            <Image src="/images/No_product_Image.jpg" className="h-full w-full" alt="empty product image" />
          )}
        </Link>
      </div>

      <div className="flex  flex-col justify-between gap-1">
        <div>
          <Link link={_url ?? '#'}>
            <Typography as="h3" className="text-[17px] !font-bold leading-[20.57px]">
              {name}
            </Typography>
          </Link>

          {bundleProducts.length > 0 &&
            bundleProducts.map((bundleProduct) => {
              return (
                <Link link={_url ?? '#'}>
                  <Typography as="p" className="mt-1.5 leading-4">
                    {bundleProduct.name}
                  </Typography>
                </Link>
              );
            })}
        </div>
        <div>
          <Link link={_url ?? '#'}>
            <Typography className=" mt-0.5 text-[15.5px] font-normal  leading-[18.76px]">{productId}</Typography>
          </Link>
        </div>
      </div>
      <div className="gap-0.5">
        <Typography as="p" className="text-[15.5px] font-normal leading-[18.76px]">
          {`Price per unit $${(((price?.centAmount as number) * getGstMultiplier(price?.currencyCode)) / 100).toFixed(2)}`}
        </Typography>
      </div>
      <div>
        <span className="text-[15.5px] font-normal leading-[18.76px] text-[#828282]">{`Colour:`}</span> &nbsp;
        <span className="text-[15.5px] font-medium leading-[18.76px]">{colour}</span>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-[15.5px] font-normal leading-[18.76px] text-[#828282]">QTY</span>
        <div>
          <QuantityWidget
            onChange={quantityChangeHandler}
            value={quantity}
            minValue={1}
            maxValue={availableQuantity}
            showLabel={false}
          />
          {quantity >= 200 && quantity < 500 && (
            <span className="mt-3 block text-[12px] font-normal leading-4 text-[#E32C53]">
              High quantity
            </span>
          )}
          {quantity >= 500 && (
            <span className="mt-3 block text-[12px] font-normal leading-4 text-[#E32C53]">
              Max quantity
            </span>
          )}
        </div>

        {qtyDisabled && <CartInSufficientStockText />}
      </div>
      <div className="row-gap-2 mt-2 grid grid-cols-2  gap-4">
        {/* <AddNote /> */}
        <RemoveButton
          className="!px-0 [&>span]:text-[15.5px] [&>span]:font-normal [&>span]:leading-[18.76px] [&>span]:text-[#828282]"
          handler={removeHandler}
        />
        {/* <PreferredDeliveryDate /> */}
      </div>
      <div className="flex items-center justify-end gap-5 border-b border-[#D3D3D3] pb-4">
        <span className="text-[15.5px] font-medium leading-[18.76px]">Price</span>
        <Typography as="p" className="text-[20px] font-semibold leading-[24.2px]">
          {`$${(((totalPrice?.centAmount as number) * getGstMultiplier(totalPrice?.currencyCode)) / 100).toFixed(2)}`}
        </Typography>
      </div>
      {showFooter && (
        <>
          {totalDiscount === 0 && <div className="flex flex-col gap-2 py-[14px]">
            <label className="text-[15.5px] font-medium leading-[18.76px]">Enter Coupon Code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                placeholder="Enter coupon code"
                className="h-[44px] flex-1 border border-[#D1D1D1] px-3 text-[14px] focus:outline-none focus:border-[#2B2C39]"
              />
              <Button
                size="custom"
                variant="secondary"
                onClick={async () => {
                  if (!couponCode.trim()) return;
                  setCouponError('');
                  const alreadyApplied = (vouchers ?? []).some(
                    (v) => v?.code?.toLowerCase() === couponCode.trim().toLowerCase(),
                  );
                  if (alreadyApplied) {
                    setCouponError('Coupon code already applied');
                    return;
                  }
                  const result = await onAddCoupon?.(couponCode.trim());
                  if (result?.data?.errorMessage === 'InvalidDiscountCode') {
                    setCouponError('Invalid Coupon Code');
                  }
                }}
                className="h-[44px] cursor-pointer !px-4 text-[15px] font-normal leading-5"
              >
                Apply
              </Button>
            </div>
            {couponError && (
              <span className="text-[13px] text-[#E32C53]">{couponError}</span>
            )}
          </div>}
          {totalDiscount > 0 && (vouchers ?? []).map((v, i) => (
            <div key={i} className="flex w-full items-center justify-end gap-4 px-0 pb-[4px] pt-[14px]">
              <span className="text-[15px] font-medium text-[#E32C53]">Discount (-{v.discount.displayAmount})</span>
              <button
                onClick={() => onRemoveCoupon?.(v.code)}
                className="cursor-pointer text-[13px] font-normal text-[#2B2C39] underline hover:opacity-70"
              >
                Remove Discount
              </button>
            </div>
          ))}
          <div className="flex  w-full justify-end gap-5 px-0 pb-[28px] pt-[14px]">
            <div className="flex flex-col items-end">
              <span className="text-[17px] font-bold leading-[35.1px]">Total Price</span>
              <span className="text-[12px] font-normal leading-[14.52px]">(inc. GST)</span>
            </div>
            {sum?.centAmount && (
              <div className="">
                <Typography as="p" className="pt-2 !text-[20px] !font-semibold !leading-[24.2px]">
                  {`$${(((sum?.centAmount as number) * getGstMultiplier(sum?.currencyCode)) / 100).toFixed(2)}`}
                </Typography>
              </div>
            )}
          </div>
          <div className="">
            <div className="flex w-full flex-col gap-4">
              <Button
                size="m"
                onClick={clearCart}
                className="border !border-black !bg-white text-[17.94px]  font-normal leading-[21.71px] !text-black"
                variant="primary"
              >
                Clear Cart
              </Button>
              <Button
                size="m"
                onClick={onCheckout}
                className="!bg-[#000000] text-[17.94px] font-normal leading-[21.71px]"
                variant="primary"
              >
                Checkout
              </Button>
            </div>
          </div>
          <div>
            <Modal
              className="flex flex-col items-center justify-center !p-10"
              isOpen={isModalOpen}
              onRequestClose={closeModal}
              preventScroll={false}
              closeButton={true}
              size="sm"
              variant="default"
              centered={true}
            >
              <>
                <Typography className="mt-3 px-4  text-center text-2xl !font-bold leading-[54.9px] text-black">
                  Insufficient Stock
                </Typography>
                <Typography className="mt-5 px-1 text-center text-base !font-medium leading-[27.1px] text-black">
                  Unfortunately, we do not have the stock available for your selected quantity. Please reduce the number in the cart or check again later when new stock is available.
                </Typography>
                <div className="flex gap-8 pb-6">
                  <Button
                    size="s"
                    variant="secondary"
                    type="submit"
                    onClick={closeModal}
                    className="mt-5 bg-[#2B2C39] !py-3 px-6 text-white "
                  >
                    Back To Cart
                  </Button>
                </div>
              </>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileCartItem;
